import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  Ban,
  BedDouble,
  ChevronRight,
  Clock,
  Dumbbell,
  GraduationCap,
  Layers,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Scissors,
  Sparkles,
  UserRound,
  Wind,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/facelift-surgery-mumbai";

const FACELIFT_FAQS = [
  {
    question: "What is the difference between a mini facelift and a full facelift?",
    answer:
      "A mini facelift uses shorter incisions and addresses early jowling and mild skin laxity. A full SMAS facelift works on the deep muscular layer and produces more comprehensive, longer-lasting correction for moderate to significant aging. Dr. Jain recommends the appropriate technique based on your degree of tissue descent.",
  },
  {
    question: "Will a facelift look natural?",
    answer:
      "Yes, when the SMAS layer is repositioned rather than the skin pulled tight. The stretched or windswept look comes from skin-only facelifts or excessive tissue tension. SMAS technique repositions tissue to where it originally sat, which produces a natural result.",
  },
  {
    question: "How long do facelift results last?",
    answer:
      "Results typically last 8 to 10 years. Aging continues after surgery, but from a reset baseline. Most patients look significantly younger than their age for years after the procedure.",
  },
  {
    question: "Is facelift surgery painful?",
    answer:
      "Surgery is performed under general anesthesia. Post-operative discomfort involves tightness and mild soreness, not sharp pain. This is managed with medication and resolves within the first week.",
  },
  {
    question: "Can a facelift be combined with other procedures?",
    answer:
      "Yes, and it frequently is. Common combinations include neck lift, eyelid surgery (blepharoplasty), cheek augmentation for volume restoration, and fat grafting. Dr. Jain discusses which combinations produce the most complete result for your specific face during consultation.",
  },
  {
    question: "When can I return to work after a facelift?",
    answer:
      "Most patients return to desk work or office settings within 2 weeks. Physical work or exercise resumes after 3 to 4 weeks.",
  },
  {
    question: "Are facelift scars visible?",
    answer:
      "No. Incisions are placed within the hairline and along the natural contours around the ear where they are not visible. Scars fade significantly within 6 to 12 months.",
  },
  {
    question: "How is a facelift different from fillers or thread lifts?",
    answer:
      "Fillers restore volume and threads provide temporary lifting. Neither corrects structural descent of the SMAS layer or removes excess skin. For patients with moderate to significant aging, a facelift produces results that non-surgical treatments cannot replicate and that last years rather than months.",
  },
];

const faceliftStructuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SurgicalProcedure",
      name: "Facelift surgery (rhytidectomy)",
      alternateName: ["SMAS facelift", "Mini facelift"],
      description:
        "Surgical lifting and repositioning of the SMAS layer and facial tissues to correct jowls, deep folds, and neck laxity with natural, long-lasting results.",
      bodyLocation: {
        "@type": "AnatomicalStructure",
        name: "Face",
      },
      followup:
        "Days 1–3: swelling, bruising, and tightness; head elevation and rest. Days 7–10: sutures removed; bruising fading. Weeks 2–3: socially presentable for most. By 6 months: final refined result as swelling fully resolves.",
      howPerformed:
        "Incisions along the hairline and around the ears; SMAS elevation and repositioning; excess skin redraped without tension; typically 3–5 hours under general anesthesia.",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: FACELIFT_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    },
  ],
};

function FaceliftJsonLd() {
  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faceliftStructuredData) }} />
  );
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Cosmetic Surgery", href: "/#services" },
    { name: "Face", href: "/#services" },
    { name: "Facelift", href: "/facelift-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Cosmetic Surgery · Face</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">
              Facelift Surgery
            </h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              SMAS Facelift &amp; Mini Facelift by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Award className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">Board Certified</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <Clock className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">17+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2 bg-premium-purple/10 px-3 py-1.5 rounded-full">
                <GraduationCap className="w-3.5 h-3.5 text-gold flex-shrink-0" />
                <span className="text-xs font-semibold text-premium-purple whitespace-nowrap">International Training</span>
              </div>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Facelift" icon="✨" size="hero"  imageSrc={HERO.facelift} imageAlt="Facelift"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">What is Facelift Surgery?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A facelift, clinically called rhytidectomy, lifts and repositions the deeper structural layers of the face,
              tightens the underlying muscles, and removes excess skin to restore a younger facial contour. It is the most
              effective surgical treatment for moderate to severe facial aging because it addresses the structural cause,
              not just the surface.
            </p>
            <p>
              Skin creams, lasers, and injectables work at the skin level. They cannot correct the muscle laxity, fat
              descent, and tissue sagging that cause jowls, deep nasolabial folds, and loss of jawline definition. Once
              aging has progressed to that structural stage, a facelift is the only procedure that produces lasting
              correction.
            </p>
            <p>
              Dr. Vicky Jain performs facelift surgery with a focus on results that look refreshed, not operated
              on. The goal is to restore the face you had 8 to 10 years ago, not to produce a different face.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">
              Dr. Jain&apos;s Approach to Facelift Surgery
            </h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                Dr. Jain uses the SMAS technique, which works on the deep muscular layer of the face rather than pulling
                skin alone. Skin-only facelifts produce the stretched, tight look patients want to avoid. SMAS elevation
                repositions the facial tissues to where they sat in a younger face, and the skin is redraped without tension.
                This is what produces a natural result.
              </p>
              <p>
                Every facelift plan is built around your specific anatomy: skin quality, degree of tissue descent, neck
                condition, and what combination of procedures, whether a neck lift, eyelid surgery, or volume restoration,
                will produce the most complete result. Patients who want subtle correction receive a mini facelift.
                Patients with moderate to significant aging receive a full SMAS lift. The plan matches the problem.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61,37,102,0) 20%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FaceliftProblemsSection() {
  const problems = [
    {
      title: "Sagging Cheeks and Jowls",
      icon: UserRound,
      copy: "Jowls form when the cheek fat pad descends below the jawline. A facelift repositions this tissue back to its original location, restoring the clean jaw contour.",
    },
    {
      title: "Deep Nasolabial Folds",
      icon: Wind,
      copy: "These folds deepen as mid-face tissue descends with age. Lifting the SMAS layer reduces the fold by restoring the support structure above it.",
    },
    {
      title: "Loss of Jawline Definition",
      icon: Layers,
      copy: "A defined jawline requires tissue to sit above the jaw, not drape over it. SMAS elevation rebuilds that structure without tension on the skin.",
    },
    {
      title: "Loose Neck Skin",
      icon: Scissors,
      copy: "Neck laxity is commonly addressed alongside a facelift with a neck lift. This tightens the platysma muscle and removes excess skin to define the neck and jaw together.",
    },
    {
      title: "Overall Facial Aging",
      icon: Sparkles,
      copy: "When multiple areas of the face show simultaneous descent, a facelift addresses all of them in a single procedure, producing a consistent, harmonious rejuvenation rather than patchy improvement.",
    },
    {
      title: "Treatments That Have Stopped Working",
      icon: Ban,
      copy: "Fillers, threads, and lasers are effective for early aging. When structural descent has progressed beyond what they can address, a facelift is the appropriate next step.",
    },
  ];

  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="facelift-problems-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Indications</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What a Facelift Corrects</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {problems.map((card, index) => (
            <div
              key={card.title}
              className="bg-white p-6 border border-[#E8E2D9]"
              data-testid={`facelift-problem-card-${index}`}
            >
              <div className="w-9 h-9 bg-gold/10 flex items-center justify-center mb-3" aria-hidden>
                <card.icon className="w-4 h-4 text-gold" />
              </div>
              <h3 className="font-serif text-lg text-premium-purple mb-3 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaceliftTypesSection() {
  const types = [
    {
      title: "Traditional SMAS Facelift",
      copy: "The gold standard for moderate to significant facial aging. Incisions run along the hairline, around the ears, and into the posterior hairline. Dr. Jain elevates the SMAS layer, repositions descended tissue, and redrapes the skin without tension. Results last 8 to 10 years. Best for patients with jowls, deep folds, and significant skin laxity.",
    },
    {
      title: "Mini Facelift",
      copy: "A shorter-scar procedure for patients with early to moderate aging who do not yet need full SMAS correction. Incisions are limited, recovery is faster, and the procedure addresses early jowling and mild skin laxity. Best for patients in their 40s or those who want correction before aging progresses further.",
    },
    {
      title: "Neck Lift",
      copy: "Addresses loose neck skin, platysmal banding, and double chin caused by skin laxity rather than fat. Frequently combined with a facelift to produce a consistent result from the jaw to the neck. Can be performed as a standalone procedure in patients whose primary concern is the neck.",
    },
    {
      title: "Non-Surgical Alternatives",
      copy: "Thread lifts and fillers address early or mild aging without surgery. They are appropriate for patients not yet ready for a facelift or those with specific isolated concerns. Results are temporary and do not replicate what surgical tissue repositioning achieves in cases of moderate to significant aging.",
    },
  ];

  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="facelift-types-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-10">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Procedure Options</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white">Types of Facelift Surgery</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {types.map((item, index) => (
            <div
              key={item.title}
              className="bg-white p-8 border border-[#E8E2D9]"
              data-testid={`facelift-type-card-${index}`}
            >
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgeryPerformedSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-widest text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple leading-tight">How Facelift Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 3 to 5 hours under general anesthesia. Incisions are placed along the hairline at the temples,
              continuing around the front of the ear, behind the earlobe, and into the posterior hairline. These positions
              conceal scars within natural contours and the hairline.
            </p>
            <p>
              Dr. Jain lifts the skin carefully, elevates and repositions the SMAS layer, tightens the underlying facial
              muscles, removes the excess skin created by tissue repositioning, and closes with fine sutures. The skin is
              not pulled tight. It is redraped over repositioned tissue, which is what eliminates the stretched appearance.
            </p>
            <p>
              Most patients stay one night and go home the following day. Drains are used in some cases to prevent fluid
              accumulation during the first 24 to 48 hours.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Swelling, bruising, and tightness are expected. Head elevation reduces swelling. Rest is the priority.",
      icon: BedDouble,
    },
    {
      period: "Days 7 to 10",
      description: "Sutures are removed. Most patients can move around normally. Bruising is still visible but fading.",
      icon: Monitor,
    },
    {
      period: "Weeks 2 to 3",
      description:
        "Presentable in social settings for most patients. Swelling continues to reduce. Residual tightness is normal.",
      icon: Dumbbell,
    },
    {
      period: "1 to 6 Months",
      description:
        "The face settles into its final position. Swelling resolves fully by month 3. The final refined result is visible by month 6.",
      icon: Sparkles,
    },
  ];

  return (
    <section className="bg-premium-purple py-14 md:py-20" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoIsGoodCandidateSection() {
  return (
    <section className="bg-warm-white pt-[50px] pb-4" data-testid="who-is-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-5 text-center">
            Who Is a Good Candidate for Facelift Surgery?
          </h2>
          <div className="space-y-4 text-deep-navy/70 leading-relaxed">
            <p>
              Most facelift patients are between 40 and 70, though age alone is not the deciding factor. The degree of
              structural facial aging is. Patients in their early 40s with significant descent benefit from earlier
              correction. Patients in their 60s who have maintained skin quality are still good candidates for a full
              SMAS lift with excellent outcomes.
            </p>
            <p>
              Ideal candidates are in good general health, non-smokers or willing to stop smoking at least 4 weeks before
              surgery, and have specific structural concerns: jowls, loss of jawline definition, deep nasolabial folds, or
              neck laxity. Patients who have tried non-surgical treatments and found the results insufficient are the most
              common facelift candidates. The right time to consider a facelift is when the structural problem becomes the
              limiting factor, not the skin surface.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Facelift" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {FACELIFT_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">
          Book Your Facelift Consultation
        </h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your skin quality, degree of tissue descent, and neck condition to
          determine which technique and combination of procedures produces the most complete result for your face. You
          leave with a clear surgical plan, realistic outcome expectations, and a full understanding of the recovery
          process.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Cheek Augmentation",
      description:
        "Restore mid-face volume alongside structural lifting for complete facial rejuvenation.",
      href: "/cheek-augmentation-in-mumbai",
    },
    {
      name: "Chin Augmentation",
      description: "Define the jawline to complement the lifted lower face.",
      href: "/chin-augmentation-in-mumbai",
    },
    {
      name: "Thread Lift",
      description: "Non-surgical lifting option for patients with early aging not yet requiring a facelift.",
      href: "/thread-lift-mumbai",
    },
  ];

  return (
    <section className="bg-warm-white py-10 md:py-14" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function FaceliftPage() {
  return (
    <>
      <SEO
        title="Facelift Surgery | Dr. Vicky Jain"
        description="Sagging skin, jowls, and deep folds need structural correction. Dr. Vicky Jain performs facelift surgery with natural, long-lasting results. Book today."
        canonical="/facelift-surgery-mumbai"
      />
      <FaceliftJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <FaceliftProblemsSection />
      <FaceliftTypesSection />
      <HowSurgeryPerformedSection />
      <RecoveryTimelineSection />
      <WhoIsGoodCandidateSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
